import FingerprintJS from '@fingerprintjs/fingerprintjs';

/** Shape we hand back to `core.ts`. Mirrors what the backend expects. */
export type FingerprintResult = {
  visitorId: string;
  hash: string;
  components: Record<string, unknown>;
};

/**
 * Run FingerprintJS once and return raw components + visitorId.
 * Returns `null` if loading or collection fails for any reason — the
 * rest of init uses `navigator.*` fallbacks so `null` is a legitimate
 * outcome, not an error.
 */
export async function collectFingerprint(): Promise<FingerprintResult | null> {
  try {
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    const components = result.components as unknown as Record<string, unknown>;
    return {
      visitorId: result.visitorId,
      hash: await sha256Hex(stableStringify(components)),
      components,
    };
  } catch {
    return null;
  }
}

async function sha256Hex(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, '0')).join(
    '',
  );
}

function stableStringify(value: unknown): string {
  if (value === null) return 'null';
  if (typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return '[' + value.map(stableStringify).join(',') + ']';

  const object = value as Record<string, unknown>;
  return (
    '{'
    + Object.keys(object)
      .sort()
      .map((key) => JSON.stringify(key) + ':' + stableStringify(object[key]))
      .join(',')
    + '}'
  );
}
